"use client";

import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { ImageIcon, Loader2 } from "lucide-react";
import { forwardRef, useRef, useState } from "react";

interface FileUploadProps {
	value?: string;
	isUploading?: boolean;
	onSelect: (file: File) => void;
	accept?: string;
}

export const FileUpload = forwardRef<HTMLDivElement, FileUploadProps>(({ value, isUploading, onSelect, accept = "image/*" }, ref) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const [preview, setPreview] = useState<string | null>(value || null);

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			onSelect(file);

			const reader = new FileReader();
			reader.onloadend = () => {
				setPreview(reader.result as string);
			};

			reader.readAsDataURL(file);
		}
	};

	return (
		<div ref={ref} className="space-y-4">
			<div
				onClick={() => inputRef.current?.click()}
				className={cn(
					"rounded-lg border-2 border-dashed p-4 transition-colors hover:border-primary/50",
					"flex cursor-pointer flex-col items-center justify-center gap-2",
					"min-h-[200px]"
				)}
			>
				{preview ? (
					<div className="relative aspect-video w-full">
						{
							// eslint-disable-next-line @next/next/no-img-element
							<img src={preview} alt="Preview" className="h-full w-full rounded-lg object-cover" />
						}
					</div>
				) : (
					<>
						<ImageIcon className="h-8 w-8 text-muted-foreground" />
						<p className="text-sm text-muted-foreground">Click to upload an image</p>
						<p className="text-sm text-muted-foreground">(16:9 ratio, 1920 x 1080)</p>
					</>
				)}

				{isUploading && (
					<div className="absolute inset-0 flex items-center justify-center bg-background/80">
						<Loader2 className="h-8 w-8 animate-spin" />
					</div>
				)}
			</div>

			<Input
				ref={inputRef}
				type="file"
				accept={accept}
				onChange={handleFileChange}
				className="hidden"
			/>
		</div>
	);
});
